import { isNil, negate, zipObject, omit } from 'lodash';
import type {
  AnalysisRequest,
  Message,
  RangeAnalysisResponse,
  RangeDebugResponse,
  FrequencyEnum,
  SubjectRangeAnalysis,
} from 'venn-api';
import { analysis } from 'venn-api';
import { type AnalysisSubject, createInvestmentRangeAnalysisKey, type UseQueryOptions } from 'venn-utils';
import { VennQueryClient, createRangeAnalysisKey, getAnalysisRequest, useQuery } from 'venn-utils';
import { create, windowScheduler } from '@yornaath/batshit';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

type QueryOptions = {
  enabled?: boolean;
  suspense?: boolean;
};

export interface RangeDebugResult {
  response: RangeDebugResponse | null;
  subjectError: Message | null;
}

export interface RangeDebugGroup {
  primary: RangeDebugResult;
  secondary?: RangeDebugResult;
  benchmark?: RangeDebugResult;
  master?: RangeDebugResult;
  maxStartTime: number;
  maxEndTime: number;
  maxFrequency: FrequencyEnum;
}

type RangeQueryResponse = {
  analysisRequest: Partial<AnalysisRequest>;
  analysisResponse: RangeAnalysisResponse;
};

const modifyUseQueryResult = (useQueryResult: UseQueryResult<RangeQueryResponse>) => {
  if (useQueryResult.data) {
    return {
      ...omit(useQueryResult, ['data', 'isLoading']),
      rangeAnalysis: useQueryResult.data?.analysisResponse,
      analysisRequest: useQueryResult.data?.analysisRequest,
      loading: useQueryResult.isLoading,
    };
  }
  return {
    ...omit(useQueryResult, ['data', 'isLoading']),
    rangeAnalysis: undefined,
    analysisRequest: undefined,
    loading: useQueryResult.isLoading,
  };
};
export type UseRangeAnalysisReturn = ReturnType<typeof modifyUseQueryResult>;
export const prefetchRangeQuery = (subject: AnalysisSubject | undefined) => {
  return VennQueryClient.getInstance().prefetchQuery(createQuery(subject));
};

export const useRangeAnalysisQuery = (
  subject: AnalysisSubject | undefined,
  options: QueryOptions = {},
): UseRangeAnalysisReturn => {
  return modifyUseQueryResult(useQuery(createQuery(subject, options)));
};

function createQuery(
  subject: AnalysisSubject | undefined,
  { suspense = false, enabled = true }: QueryOptions = {},
): UseQueryOptions<RangeQueryResponse> {
  return {
    staleTime: 5 * 60 * 1000,
    queryKey: createRangeAnalysisKey(subject),
    suspense,
    enabled,
    queryFn: async ({ signal }) => {
      if (!subject || subject.private) {
        return Promise.reject(new Error('Range analysis must have a subject'));
      }

      const analysisRequest = getAnalysisRequest(['RANGE_ANALYSIS'], subject, {}, false, true);
      const response = await analysis(analysisRequest, signal);
      const analysisResponse = response.content.analyses.map((a) => a.rangeAnalysis).find(negate(isNil));
      if (analysisRequest && analysisResponse) {
        return { analysisRequest, analysisResponse };
      }
      return Promise.reject(new Error('Range analysis not found'));
    },
  };
}

type RangeAnalysisSubject = {
  id: string;
  proxySetId: string | undefined;
};
export const useRangeAnalysisQueryBatched = (funds: RangeAnalysisSubject[], options: QueryOptions = {}) => {
  const results = useQueries({
    queries: funds.map(({ id, proxySetId }) => createSingleRangeQuery(id, proxySetId, options)),
  });
  return zipObject(
    funds.map(({ id }) => id),
    results.map(({ data }) => data),
  );
};

const batchFetcher = create({
  fetcher: async (inputs: { investmentId: string; proxySetId: string | undefined }[]) => {
    const response = await analysis({
      analyses: [{ analysisType: 'RANGE_ANALYSIS' }],
      subjects: inputs.map(({ investmentId: id, proxySetId }) => ({
        id,
        subjectType: 'INVESTMENT',
        primary: true,
        comparisonType: 'PRIMARY',
        isPrivate: false,
        proxySetId: proxySetId ?? null,
      })),
    });
    const analysisResponse = response.content.analyses.map((a) => a.rangeAnalysis).find(negate(isNil));
    return analysisResponse?.rangeAnalyses ?? [];
  },
  resolver: (rangeAnalyses, { investmentId }) =>
    rangeAnalyses.find((analysis) => analysis.investmentId === investmentId)!,
  scheduler: windowScheduler(10),
});

function createSingleRangeQuery(
  fundId: string,
  proxySetId: string | undefined,
  { suspense = true, enabled = true }: QueryOptions = {},
): UseQueryOptions<SubjectRangeAnalysis> {
  return {
    staleTime: 5 * 60 * 1000,
    queryKey: createInvestmentRangeAnalysisKey(fundId, proxySetId),
    suspense,
    enabled,
    queryFn: () => batchFetcher.fetch({ investmentId: fundId, proxySetId }),
  };
}
